import { ContentType } from '@quromedical/fhir-common';
import type { FileStorageClient } from '@quromedical/storage';
import { z } from 'zod';

const UUIDSchema = z.string().uuid();

const DateStringSchema = z.string().superRefine((value) => {
  return !isNaN(Date.parse(value));
});

export enum MessageType {
  Text = 'Text',
  Media = 'Media',
}

export enum MediaMessageType {
  Image = 'Image',
  Video = 'Video',
  Audio = 'Audio',
  Document = 'Document',
}

export enum TemplateNames {
  VideoConsultInvitation = 'Video Consult Invitation',
  ClinicalNoteNotification = 'ClinicalNoteNotification',
  NewPatientAssignment = 'NewPatientAssignment',
  PractitionerWelcomeInvite = 'PractitionerWelcomeInvite',
  PatientMedicationRequest = 'PatientMedicationRequest',
  NewExternalNote = 'NewExternalNote',
  TCPWelcomeMessage = 'TCPWelcomeMessage',
  TCPCollectionDischarge = 'TCPCollectionDischarge',
  TCPCollectionDay8 = 'TCPCollectionDay8',
  TCPVitalCollection = 'TCPVitalCollection',
  PreOnboardingMessage = 'PreOnboardingMessage',

  //ConversationTemplates
  IntroductoryMessageToOnboardings = 'Introductory message to onboardings',
  NormalVitals = 'Normal vitals',
  AbnormalityInVitalsNoUrgentActionNeeded = 'Abnormality in vitals no urgent action needed',
  AbnormalityInVitalsUrgentActionNeeded = 'Abnormality in vitals urgent action needed',
  MissedVirtualVisit = 'Missed virtual visit',
  PatientMIA = 'Patient MIA',
  NonCompliantWithVirtualVisits = 'Non compliant with virtual visits',
  NotMonitoring = 'Not monitoring',
  TechCalloutRePatching = 'Tech callout re-patching',
  ClinicalCallout = 'Clinical callout',
  QualitySurvey = 'Quality Survey',
  SevenDayReportSurvey = 'Seven Day Report Survey',
}

export const MessageTypeSchema = z.nativeEnum(MessageType);
export const ContentTypeSchema = z.nativeEnum(ContentType);

export const BaseParticipantSchema = z.object({
  identifier: z.string().describe('The identifier of the participant'),
  name: z.string().optional().describe('The name of the participant'),
  avatar: z.string().url().optional().describe('The avatar of the participant'),
  phoneNumber: z.string().describe('The phone number of the participant'),
});

export const ParticipantSchema = BaseParticipantSchema.extend({
  id: z.string().describe('The id of the participant'),
});

export const ReadReceiptSchema = z.object({
  participantId: z.string().describe('The id of the participant'),
  readAt: DateStringSchema,
});

export const BaseConversationMessageSchema = z.object({
  id: z.string().describe('The id of the message'),
  conversationId: z.string().describe('The id of the conversation'),
  createdAt: DateStringSchema,
  delivered: z.boolean().describe('Whether the message was delivered'),
  read: z.boolean().describe('Whether the message was read'),
  sender: z
    .string()
    .describe('The sender of the message')
    .optional()
    .default('system'),
});

export const TextConversationMessageSchema =
  BaseConversationMessageSchema.extend({
    type: z.literal(MessageType.Text),
    text: z.string().describe('The text of the message'),
  });

export const MediaConversationSchema = z.object({
  type: z.literal(MessageType.Media),
  contentType: ContentTypeSchema,
  url: z.string().url().optional().describe('The url of the media'),
  content: z.string().optional().describe('The media content'),
  mediaKey: z.string().optional().describe('The media key'),
  title: z.string().optional().describe('The media title'),
});

export const MediaConversationMessageSchema =
  BaseConversationMessageSchema.extend(MediaConversationSchema.shape);

export const ConversationMessageSchema = z.discriminatedUnion('type', [
  TextConversationMessageSchema,
  MediaConversationMessageSchema,
]);

export const BaseCreateTextMessageSchema = z.object({
  type: z.literal(MessageType.Text),
  text: z.string().describe('The text of the message'),
});

export const BaseCreateMediaMessageSchema = z.object({
  type: z.literal(MessageType.Media),
  contentType: ContentTypeSchema.describe('The type of the media'),
  url: z.string().url().optional().describe('The url of the media'),
  text: z.string().optional().describe('The text of the message'),
  content: z.string().optional().describe('The text of the message'),
  title: z.string().describe('The text of the title'),
  mediaKey: z.string().describe('The text of the key'),
});

const ParticipantIdSchema = z.string().or(z.literal('system'));
export const CreateTextMessageSchema = BaseCreateTextMessageSchema.extend({
  participantId: ParticipantIdSchema,
});

export const CreateMediaMessageSchema = BaseCreateMediaMessageSchema.extend({
  participantId: ParticipantIdSchema,
});

export const CreateMessageInputSchema = z.discriminatedUnion('type', [
  CreateTextMessageSchema,
  CreateMediaMessageSchema,
]);

export const BaseCreateMessageInputSchema = z.discriminatedUnion('type', [
  BaseCreateTextMessageSchema,
  BaseCreateMediaMessageSchema,
]);

export const ConversationSendMessageSchema = z.object({
  patient: UUIDSchema,
  nextOfKin: UUIDSchema.optional(),
  message: BaseCreateMessageInputSchema,
});

export const ConversationSendTemplateMessageSchema = z.object({
  patient: UUIDSchema,
  nextOfKin: UUIDSchema.optional(),
  template: z.nativeEnum(TemplateNames),
});

export const ConversationSchema = z.object({
  id: z.string().describe('The id of the conversation'),
  participants: ParticipantSchema.array().describe(
    'The participants of the conversation',
  ),
  createdAt: DateStringSchema,
});

export const GetConversationsInputSchema = z.object({
  limit: z.number().gte(1).lte(100).optional(),
  nextToken: z.string().optional(),
});

export const GetConversationAlertInputSchema = z.object({
  patient: UUIDSchema.optional(),
});
export const ConversationListSchema = z.object({
  conversations: ConversationSchema.array(),
  nextToken: z.string().optional(),
});

// Suggest a better name
export const ConversationSendMessageRequestSchema =
  ConversationSendMessageSchema.or(ConversationSendTemplateMessageSchema);

export type Conversation = z.infer<typeof ConversationSchema>;

export type BaseParticipant = z.infer<typeof BaseParticipantSchema>;

export type Participant = z.infer<typeof ParticipantSchema>;

export type ReadReceipt = z.infer<typeof ReadReceiptSchema>;

export type BaseMessage = z.infer<typeof BaseConversationMessageSchema>;

export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

export type WithPartialText<T> = T & { text?: string };

export type TextConversationMessage = z.infer<
  typeof TextConversationMessageSchema
>;

export type MediaConversation = z.infer<typeof MediaConversationSchema>;

export type MediaConversationMessage = z.infer<
  typeof MediaConversationMessageSchema
>;

export type CreateConversationMessageInput = z.infer<
  typeof CreateMessageInputSchema
>;

export type CreateTextConversationMessageInput = z.infer<
  typeof CreateTextMessageSchema
>;

export type CreateMediaConversationMessageInput = z.infer<
  typeof CreateMediaMessageSchema
>;

export type GetConversationsInput = z.infer<typeof GetConversationsInputSchema>;

export type ConversationList = z.infer<typeof ConversationListSchema>;

export type ConversationSendMessage = z.infer<
  typeof ConversationSendMessageSchema
>;

export type ConversationSendTemplateMessage = z.infer<
  typeof ConversationSendTemplateMessageSchema
>;

// Suggest a better name
export type ConversationSendMessageRequest = z.infer<
  typeof ConversationSendMessageRequestSchema
>;

export interface ConversationClient {
  readonly name: string;
  readonly system: string;
  /**
   * Create a conversation with the given participant
   *
   * @param participant - The participant to create the conversation with
   */
  createParticipantConversation(
    participant: BaseParticipant,
  ): Promise<Conversation>;
  /**
   * Insert or update a conversation with the given participant
   *
   * @param participant - The participant to upsert the conversation with
   */
  upsertParticipantConversation(
    participant: BaseParticipant,
  ): Promise<Conversation>;
  /**
   * update a conversation with the given info
   *
   * @param newParticipant - The participant details to update with
   */
  updateConversation(
    conversationId: string,
    newParticipant: BaseParticipant,
  ): Promise<Conversation>;
  /**
   * update a participant with the given info
   *
   * @param currentParticipant - The participant with old details
   * @param newParticipant - The participant details to update with
   */
  updateConversationParticipant(
    conversationId: string,
    currentParticipant: BaseParticipant,
    newParticipant: BaseParticipant,
  ): Promise<Conversation>;
  /**
   * Add a message to the conversation
   *
   * @param id  - The id of the conversation
   * @param message - The message to add to the conversation
   */
  addConversationMessage(
    id: string,
    message: CreateConversationMessageInput,
    storageClient?: FileStorageClient,
  ): Promise<ConversationMessage>;
  /**
   * Add participants to the conversation
   *
   * @param id = The id of the conversation
   * @param participants  - The participants to add to the conversation
   */
  addConversationParticipants(
    id: string,
    participants: BaseParticipant[],
  ): Promise<Participant[]>;
  /**
   * Add participant to the conversation
   *
   * @param id = The id of the conversation
   * @param participant  - The participant to add to the conversation
   */
  addConversationParticipant(
    id: string,
    participant: BaseParticipant,
  ): Promise<Participant>;
  /**
   * Get the conversation by id
   *
   * @param id - The id of the conversation
   * @returns The conversation
   */
  getConversationById(id: string): Promise<Conversation>;
  /**
   * Add a conversation template message
   *
   * @param id - The id of the conversation
   * @param name - The name of the template message
   */
  addConversationTemplateMessage(
    id: string,
    templateId: string,
    contentVariables?: Record<string, string>,
  ): Promise<ConversationMessage>;
  /**
   * Get the participant conversation
   */
  getParticipantConversations(
    participant: Participant,
  ): Promise<Conversation[]>;

  getConversationParticipants(conversationId: string): Promise<Participant[]>;

  getMedia(mediaId: string, conversationId: string): Promise<MediaConversation>;
  /**
   * Get the conversations
   *
   * @param input - The input to get the conversations
   */
  getConversations(input: GetConversationsInput): Promise<ConversationList>;
  /**
   *
   * @param id -
   */
  getConversationMessages(id: string): Promise<ConversationMessage[]>;
  /**
   * Remove a conversation by id
   *
   * @param id - The id of the conversation
   * @returns void
   */
  removeConversation(id: string): Promise<void>;
}
